import MdLicenseBanner from "../components/MdLicenseBanner";
import { useState, useEffect, useCallback } from 'react';
import {
  Eye, RefreshCw, Search, Monitor, Camera, Server, Router,
  Network, HardDrive, Wifi, Activity, Clock, Shield,
  Loader2, Plus, Settings, Pause, Play, Wrench, Trash2,
  X, CheckCircle, AlertTriangle, XCircle, ChevronDown, Edit3, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import monitoredDevicesTutorial from '../components/tutorials/configs/monitoredDevicesTutorial';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import StatusTooltip from '../components/StatusTooltip';
import { useMdStatusUpdates } from '../hooks/useMdStatusUpdates';
import useAuthStore from '../stores/authStore';
import MdAddDeviceModal from '../components/MdAddDeviceModal';
import '../styles/v2/pages/md-add-device-modal.css';

import DevicesTabBar from '../components/DevicesTabBar';
const deviceIcons = {
  CAMERA: Camera, SERVER: Server, ROUTER: Router, SWITCH: Network,
  NVR: HardDrive, UNKNOWN: Monitor, ACCESS_POINT: Wifi,
  ACCESS_CONTROLLER: Activity, INTERCOM: Activity, PRINTER: Activity,
};

const statusConfig = {
  ONLINE: { icon: CheckCircle, color: '#00d4aa', label: 'Online' },
  OFFLINE: { icon: XCircle, color: '#ff4d4d', label: 'Offline' },
  DEGRADED: { icon: AlertTriangle, color: '#ffaa00', label: 'Degraded' },
  MAINTENANCE: { icon: Wrench, color: '#888', label: 'Maintenance' },
  UNKNOWN: { icon: Monitor, color: '#555', label: 'Unknown' },
  PAUSED: { icon: Pause, color: '#666', label: 'Paused' },
};

const profileLabels = {
  PING: 'Ping', HTTP_CHECK: 'HTTP', RTSP_CHECK: 'RTSP',
  API_HEALTH: 'API', SNMP_POLL: 'SNMP', WMI: 'WMI', REDFISH: 'Redfish', CUSTOM: 'Custom',
};

const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Natural IP sort: 192.168.1.2 before 192.168.1.10
function compareIPs(a, b) {
  const pa = (a || "").split(".").map(Number);
  const pb = (b || "").split(".").map(Number);
  for (let i = 0; i < 4; i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
  }
  return 0;
}

const MdMonitoredDevices = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(monitoredDevicesTutorial);

  const navigate = useNavigate();
  const { tenantVersion } = useAuthStore();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('');
  

  // Edit modal state
  const [editDevice, setEditDevice] = useState(null);
  const [editConfig, setEditConfig] = useState({});
  const [saving, setSaving] = useState(false);

  // Add manual device modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());

  const toggleCard = (deviceId) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const [syncing, setSyncing] = useState(false);

  // Real-time status updates via WebSocket
  useMdStatusUpdates(useCallback((update) => {
    if (update.type === 'HEALTH_RESULT') {
      setDevices(prev => prev.map(d =>
        d.id === update.deviceId ? {
          ...d,
          status: update.status,
          lastCheckAt: update.checkedAt,
          lastLatencyMs: update.latencyMs,
          consecutiveFailures: update.consecutiveFailures,
          authFailed: update.authFailed || false,
        } : d
      ));
    } else if (update.type === 'STATUS_CHANGE') {
      setDevices(prev => prev.map(d =>
        d.id === update.deviceId ? {
          ...d,
          status: update.newStatus,
          lastLatencyMs: update.latencyMs,
        } : d
      ));
    }
  }, []));



  // Reset on tenant switch
  useEffect(() => {
    setLoading(true);
  }, [tenantVersion]);

  const fetchDevices = useCallback(async (silent = false) => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);

      if (searchTerm) params.set('search', searchTerm);
      const url = `/api/md/monitored${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetchWithTenant(url);
      if (response.ok) {
        const data = await response.json();
        setDevices((data.devices || []).map(d => ({
          ...d,
          authFailed: d.pollHistory?.some(p => 
            p.error?.includes('auth failed') || 
            p.error?.includes('credentials rejected') || 
            p.error?.includes('credential issue')
          ) || false
        })));
      }
    } catch (error) {
      console.error('Failed to fetch monitored devices:', error);
      toast.error('Failed to load monitored devices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  const handleSyncIntegrations = async () => {
    setSyncing(true);
    try {
      const response = await fetchWithTenant('/api/md/monitored/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaults: {} }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const msgs = [];
        if (data.totalSynced > 0) msgs.push(`${data.totalSynced} devices synced`);
        if (data.totalSkipped > 0) msgs.push(`${data.totalSkipped} already existed`);
        if (data.totalErrors > 0) msgs.push(`${data.totalErrors} errors`);
        toast.success(msgs.join(', ') || 'Sync complete (no new devices)');
        fetchDevices();
      } else {
        toast.error(data.error || 'Sync failed');
      }
    } catch (err) {
      toast.error('Failed to sync integrations');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    // Silent refresh - don't show loading spinner or blank the list on interval refresh
    const interval = setInterval(() => fetchDevices(true), 60000); // Fallback poll - real-time via WebSocket
    return () => clearInterval(interval);
  }, [fetchDevices]);

  // Stats - by device type
  const stats = {
    total: devices.length,
    cameras: devices.filter(d => d.deviceType === 'CAMERA').length,
    accessControl: devices.filter(d => ['ACCESS_CONTROLLER', 'DOOR_CONTROLLER'].includes(d.deviceType)).length,
    servers: devices.filter(d => d.deviceType === 'SERVER').length,
    network: devices.filter(d => ['ROUTER', 'SWITCH', 'ACCESS_POINT'].includes(d.deviceType)).length,
  };

  // Get unique groups
  const groups = [...new Set(devices.map(d => d.group).filter(Boolean))];

  // Filter
  const filteredDevices = devices.filter(device => {
    if (groupFilter && device.group !== groupFilter) return false;
    if (statusFilter && device.status !== statusFilter) return false;
    // Type filter from stat cards
    if (typeFilter === 'cameras' && device.deviceType !== 'CAMERA') return false;
    if (typeFilter === 'accessControl' && !['ACCESS_CONTROLLER', 'DOOR_CONTROLLER'].includes(device.deviceType)) return false;
    if (typeFilter === 'servers' && device.deviceType !== 'SERVER') return false;
    if (typeFilter === 'network' && !['ROUTER', 'SWITCH', 'ACCESS_POINT'].includes(device.deviceType)) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!device.name?.toLowerCase().includes(s) &&
          !device.ipAddress?.toLowerCase().includes(s) &&
          !device.hostname?.toLowerCase().includes(s) &&
          !device.manufacturer?.toLowerCase().includes(s)) return false;
    }
    return true;
  }).sort((a, b) => compareIPs(a.ipAddress, b.ipAddress));

  // Actions
  const handleToggleMonitoring = async (device) => {
    try {
      const response = await fetchWithTenant(`/api/md/monitored/${device.id}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !device.monitoringEnabled }),
      });
      if (response.ok) {
        toast.success(device.monitoringEnabled ? 'Monitoring paused' : 'Monitoring resumed');
        fetchDevices();
      }
    } catch (err) {
      toast.error('Failed to toggle monitoring');
    }
  };

  const handleMaintenance = async (device) => {
    const entering = device.status !== 'MAINTENANCE';
    try {
      const response = await fetchWithTenant(`/api/md/monitored/${device.id}/maintenance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: entering,
          note: entering ? 'Manual maintenance' : null,
        }),
      });
      if (response.ok) {
        toast.success(entering ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
        fetchDevices();
      }
    } catch (err) {
      toast.error('Failed to set maintenance mode');
    }
  };

  const handleDelete = async (device) => {
    if (!confirm(`Remove "${device.name}" from monitoring? This won't delete the discovered device.`)) return;
    try {
      const response = await fetchWithTenant(`/api/md/monitored/${device.id}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Device removed from monitoring');
        fetchDevices();
      }
    } catch (err) {
      toast.error('Failed to remove device');
    }
  };

  const handleEdit = (device) => {
    setEditDevice(device);
    setEditConfig({
      name: device.name,
      group: device.group || '',
      monitoringProfile: device.monitoringProfile,
      pollIntervalSeconds: device.pollIntervalSeconds,
      alertThresholdSeconds: device.alertThresholdSeconds,
      deviceType: device.deviceType,
      certMonitoringEnabled: device.certMonitoringEnabled || false,
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await fetchWithTenant(`/api/md/monitored/${editDevice.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editConfig),
      });
      if (response.ok) {
        toast.success('Device updated');
        setEditDevice(null);
        fetchDevices();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update device');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      {/* Header */}
      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title"><Eye size={28} /> Devices</h1>
          <p className="page-subtitle">Monitored devices and health status</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={handleSyncIntegrations} disabled={syncing}
              title="Import cameras and doors from Wave VMS and Feenics">
            {syncing ? <><Loader2 size={16} className="spin" /> Syncing...</> : <><RefreshCw size={16} /> Sync Integrations</>}
          </button>
          <button className="btn btn-accent" onClick={() => setShowAddModal(true)}>
            <Plus size={16} /> Add Device
          </button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(monitoredDevicesTutorial.module, monitoredDevicesTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      <MdLicenseBanner />
        <DevicesTabBar />

      {/* Stats Cards */}
      <div className="md-stats-grid">
        <div className={`md-stat-card ${typeFilter === 'all' ? 'active' : ''}`} onClick={() => setTypeFilter('all')}>
          <div className="md-stat-icon total"><Eye size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.total}</span>
            <span className="md-stat-label">Total Monitored</span>
          </div>
        </div>
        <div className={`md-stat-card ${typeFilter === 'cameras' ? 'active' : ''}`} onClick={() => setTypeFilter(typeFilter === 'cameras' ? 'all' : 'cameras')}>
          <div className="md-stat-icon" style={{background: 'rgba(59,130,246,0.15)', color: '#3b82f6'}}><Camera size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.cameras}</span>
            <span className="md-stat-label">Cameras</span>
          </div>
        </div>
        <div className={`md-stat-card ${typeFilter === 'accessControl' ? 'active' : ''}`} onClick={() => setTypeFilter(typeFilter === 'accessControl' ? 'all' : 'accessControl')}>
          <div className="md-stat-icon" style={{background: 'rgba(168,85,247,0.15)', color: '#a855f7'}}><Shield size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.accessControl}</span>
            <span className="md-stat-label">Access Control</span>
          </div>
        </div>
        <div className={`md-stat-card ${typeFilter === 'servers' ? 'active' : ''}`} onClick={() => setTypeFilter(typeFilter === 'servers' ? 'all' : 'servers')}>
          <div className="md-stat-icon" style={{background: 'rgba(234,179,8,0.15)', color: '#eab308'}}><Server size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.servers}</span>
            <span className="md-stat-label">Servers</span>
          </div>
        </div>
        <div className={`md-stat-card ${typeFilter === 'network' ? 'active' : ''}`} onClick={() => setTypeFilter(typeFilter === 'network' ? 'all' : 'network')}>
          <div className="md-stat-icon" style={{background: 'rgba(20,184,166,0.15)', color: '#14b8a6'}}><Network size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.network}</span>
            <span className="md-stat-label">Network</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="md-filters">
        <div className="md-search">
          <Search size={16} />
          <input type="text" className="form-input" placeholder="Search by name, IP, hostname..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {groups.length > 0 && (
          <select className="form-select" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
            <option value="">All Groups</option>
            {groups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
        <button className="btn btn-secondary btn-sm" onClick={fetchDevices} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Devices Table */}
      <div className="card">
        {loading ? (
          <div className="md-loading"><Loader2 size={32} className="spin" /><p>Loading monitored devices...</p></div>
        ) : filteredDevices.length === 0 ? (
          <div className="md-empty">
            <Eye size={48} />
            <h3>No monitored devices</h3>
            <p>{devices.length === 0 ? 'Add devices from Discovery or manually to start monitoring.' : 'No devices match your filters.'}</p>
            {devices.length === 0 && (
              <button className="btn btn-accent" onClick={() => setShowAddModal(true)}>
                <Plus size={16} /> Add Device
              </button>
            )}
          </div>
        ) : (
          <>
          {/* ─── Mobile Card View ─── */}
            <div className="md-mobile-cards">
              {filteredDevices.map(device => {
                const DeviceIcon = deviceIcons[device.deviceType] || Monitor;
                const status = statusConfig[device.status] || statusConfig.UNKNOWN;
                const StatusIcon = status.icon;
                const isExpanded = expandedCards.has(device.id);
                return (
                  <div
                    key={device.id}
                    className={`md-mobile-card ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleCard(device.id)}
                  >
                    <div className="md-mcard-main">
                      <div className="md-mcard-row1">
                        <div className="md-mcard-name-wrap">
                          <DeviceIcon size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                          <div className="md-mcard-name-col">
                            <span className="md-mcard-name">{device.name}</span>
                            {device.group && <span className="md-mcard-group">{device.group}</span>}
                          </div>
                        </div>
                        <span
                          className="md-status-pill"
                          style={{ color: status.color, borderColor: status.color + '40', background: status.color + '15' }}
                        >
                          <StatusIcon size={12} /> {status.label}
                        </span>
                        <ChevronDown size={14} className={`md-mcard-chevron ${isExpanded ? 'open' : ''}`} />
                      </div>
                      <div className="md-mcard-row2">
                        <code>{device.ipAddress}</code>
                      </div>
                    </div>
                    <div className={`md-mcard-expand ${isExpanded ? 'open' : ''}`}>
                      <div className="md-mcard-details">
                        <div className="md-mcard-detail">
                          <span className="md-mcard-detail-label">Profile</span>
                          <span className="md-mcard-detail-value">{profileLabels[device.monitoringProfile] || device.monitoringProfile}</span>
                        </div>
                        <div className="md-mcard-detail">
                          <span className="md-mcard-detail-label">Latency</span>
                          <span className="md-mcard-detail-value">{device.lastLatencyMs != null ? device.lastLatencyMs + 'ms' : '---'}</span>
                        </div>
                        <div className="md-mcard-detail">
                          <span className="md-mcard-detail-label">Last check</span>
                          <span className="md-mcard-detail-value">{formatRelativeTime(device.lastCheckAt)}</span>
                        </div>
                        <div className="md-mcard-detail">
                          <span className="md-mcard-detail-label">Hostname</span>
                          <span className="md-mcard-detail-value">{device.hostname || '---'}</span>
                        </div>
                        <div className="md-mcard-actions">
                          <button className="md-mcard-action-btn" onClick={(e) => { e.stopPropagation(); navigate(`/monitoring/devices/${device.id}`); }}>
                            <Eye size={14} /> View
                          </button>
                          <button className="md-mcard-action-btn" onClick={(e) => { e.stopPropagation(); handleEdit(device); }}>
                            <Settings size={14} /> Edit
                          </button>
                          <button className="md-mcard-action-btn danger" onClick={(e) => { e.stopPropagation(); handleDelete(device); }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ─── Desktop Table View ─── */}
            <div className="md-table-container md-monitored-table-container">
            <table className="md-devices-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>IP Address</th>
                  <th>Status</th>
                  <th>Profile</th>
                  <th>Latency</th>
                  <th>Last Check</th>
                  <th className="md-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map(device => {
                  const DeviceIcon = deviceIcons[device.deviceType] || Monitor;
                  const status = statusConfig[device.status] || statusConfig.UNKNOWN;
                  const StatusIcon = status.icon;
                  return (
                    <tr key={device.id} onClick={() => navigate(`/monitoring/devices/${device.id}`)} style={{ cursor: "pointer" }}>
                      <td>
                        <div className="md-device-name-cell">
                          <DeviceIcon size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                          <div>
                            <span className="md-device-name">{device.name}</span>
                            {device.group && <span className="md-device-group">{device.group}</span>}
                          </div>

                        </div>
                      </td>
                      <td>
                        <div className="md-ip-cell">
                          <code>{device.ipAddress}</code>
                          {device.hostname && <span className="md-mac">{device.hostname}</span>}
                        </div>
                      </td>
                      <td>
                        <StatusTooltip status={device.status}>
                        <span 
                          className={`md-status-pill ${device.authFailed ? 'auth-warning' : ''}`}
                          style={device.authFailed 
                            ? { background: 'linear-gradient(90deg, #00c853 50%, #ffaa00 50%)', color: '#fff', borderColor: '#ffaa0060' }
                            : { color: status.color, borderColor: status.color + '40', background: status.color + '15' }
                          }
                        >
                          <StatusIcon size={12} /> {device.authFailed ? 'Auth Error' : status.label}
                        </span>
                        </StatusTooltip>
                      </td>
                      <td><span className="md-profile-badge">{profileLabels[device.monitoringProfile] || device.monitoringProfile}</span></td>
                      <td>{device.lastLatencyMs != null ? `${device.lastLatencyMs}ms` : '—'}</td>
                      <td>
                        <span className="md-time-cell"><Clock size={12} />{formatRelativeTime(device.lastCheckAt)}</span>
                      </td>
                      <td className="md-actions-col">
                        <div className="md-action-btns">
                          <button className="md-action-btn" onClick={(e) => { e.stopPropagation(); handleEdit(device); }} title="Edit config">
                            <Settings size={14} />
                          </button>
                          <button className="md-action-btn danger" onClick={(e) => { e.stopPropagation(); handleDelete(device); }} title="Remove">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
        )}

        {!loading && filteredDevices.length > 0 && (
          <div className="md-table-footer">
            Showing {filteredDevices.length} of {devices.length} monitored devices
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editDevice && (
        <div className="modal-overlay" onClick={() => setEditDevice(null)}>
          <div className="modal-content md-promote-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Settings size={20} /> Edit — {editDevice.name}</h2>
              <button className="modal-close" onClick={() => setEditDevice(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="md-promote-form">
                <div className="form-group">
                  <label>Device Name</label>
                  <input type="text" className="form-input" value={editConfig.name}
                    onChange={(e) => setEditConfig(c => ({ ...c, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Group</label>
                  <input type="text" className="form-input" placeholder="e.g. Building A"
                    value={editConfig.group} onChange={(e) => setEditConfig(c => ({ ...c, group: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Device Type</label>
                  <select className="form-select" value={editConfig.deviceType}
                    onChange={(e) => setEditConfig(c => ({ ...c, deviceType: e.target.value }))}>
                    <option value="UNKNOWN">Unknown</option>
                    <option value="CAMERA">Camera</option>
                    <option value="NVR">NVR</option>
                    <option value="DVR">DVR</option>
                    <option value="VMS_SERVER">VMS Server</option>
                    <option value="ACCESS_CONTROLLER">Access Controller</option>
                    <option value="INTERCOM">Intercom</option>
                    <option value="SWITCH">Switch</option>
                    <option value="ROUTER">Router</option>
                    <option value="ACCESS_POINT">Access Point</option>
                    <option value="FIREWALL">Firewall</option>
                    <option value="SERVER">Server</option>
                    <option value="WORKSTATION">Workstation</option>
                    <option value="IOT_DEVICE">IoT Device</option>
                    <option value="PRINTER">Printer</option>
                    <option value="NAS">NAS</option>
                    <option value="UPS">UPS</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editConfig.certMonitoringEnabled}
                      onChange={(e) => setEditConfig(c => ({ ...c, certMonitoringEnabled: e.target.checked }))}
                      style={{ accentColor: '#00d4aa' }}
                    />
                    Certificate Monitoring
                  </label>
                  <span className="form-hint">Monitor SSL/TLS certificate expiry on this device</span>
                </div>
                <div className="form-group">
                  <label>Monitoring Profile</label>
                  <select className="form-select" value={editConfig.monitoringProfile}
                    onChange={(e) => setEditConfig(c => ({ ...c, monitoringProfile: e.target.value }))}>
                    <option value="PING">Ping (ICMP)</option>
                    <option value="HTTP_CHECK">HTTP Health Check</option>
                    <option value="RTSP_CHECK">RTSP Stream Check</option>
                    <option value="API_HEALTH">API Health Check</option>
                    <option value="SNMP_POLL">SNMP Poll</option>
                    <option value="WMI">WMI (Windows)</option>
                    <option value="REDFISH">Redfish (BMC/IPMI)</option>
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Poll Interval</label>
                    <select className="form-select" value={editConfig.pollIntervalSeconds}
                      onChange={(e) => setEditConfig(c => ({ ...c, pollIntervalSeconds: parseInt(e.target.value) }))}>
                      <option value={30}>Every 30s</option>
                      <option value={60}>Every 1 min</option>
                      <option value={120}>Every 2 min</option>
                      <option value={300}>Every 5 min</option>
                      <option value={600}>Every 10 min</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Alert After</label>
                    <select className="form-select" value={editConfig.alertThresholdSeconds}
                      onChange={(e) => setEditConfig(c => ({ ...c, alertThresholdSeconds: parseInt(e.target.value) }))}>
                      <option value={60}>1 min offline</option>
                      <option value={120}>2 min offline</option>
                      <option value={300}>5 min offline</option>
                      <option value={600}>10 min offline</option>
                      <option value={900}>15 min offline</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditDevice(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleSaveEdit} disabled={saving}>
                {saving ? <><Loader2 size={16} className="spin" /> Saving...</> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}


            {/* Add Manual Device Modal */}
      <MdAddDeviceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onDeviceAdded={() => fetchDevices()}
        existingGroups={[...new Set(devices?.map(d => d.group).filter(Boolean) || [])]}
      />

      {showPrompt && (
        <TutorialPrompt
          module={monitoredDevicesTutorial.module}
          moduleName={monitoredDevicesTutorial.moduleName}
          steps={monitoredDevicesTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default MdMonitoredDevices;